import { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Copy, Check, X, Loader2 } from "lucide-react";

import api from "../../api/api";
import { toast } from "react-toastify";
import useWebSocket from "../../hooks/useWebSocket";

import WarningModal from "./WarningModal";
import TransactionStatusModal from "./TransactionStatusModal";
import PaymentProcessingModal from "./PaymentProcessingModal";

import { useTranslation } from "../../hooks/useTranslation";
import { formatCurrency, minorToMajor } from "../../i18n/utils/helpers";
import { formatDateOnly, darkenColor } from "../../utils/commonUtils";
import { safeRedirect } from "../../utils/redirectUrlBuilder";

import pixLogo from "../../assets/payment-logos/pix.png";

// ─── Animations ──────────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

// ─── Layout ──────────────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  animation: ${fadeIn} 0.2s ease;
`;

const Card = styled.div`
  width: 100%;
  max-width: 420px;
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 20px 60px -10px rgba(15, 23, 42, 0.25);
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI",
    Helvetica, Arial, sans-serif;
  animation: ${slideUp} 0.3s ease;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
`;

const MerchantBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const LogoCircle = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${(p) => p.$color || "#0f172a"};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const LogoInitial = styled.span`
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
`;

const MerchantName = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
  letter-spacing: -0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 6px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: #f1f5f9;
    color: #475569;
  }
`;

// ─── Content sections ───────────────────────────────────────────────────────

const Body = styled.div`
  padding: 24px 20px 20px;
`;

const PixBadge = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 18px;

  img {
    height: 28px;
    width: auto;
    object-fit: contain;
  }
`;

const Amount = styled.div`
  text-align: center;
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #0f172a;
  margin: 0 0 4px;
`;

const ExpiryLine = styled.p`
  text-align: center;
  font-size: 13px;
  color: #64748b;
  margin: 0 0 24px;
`;

const QrCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 18px;
`;

const QrInstruction = styled.p`
  font-size: 13px;
  color: #475569;
  margin: 0 0 14px;
  font-weight: 500;
  text-align: center;
`;

const QrFrame = styled.div`
  background: #fff;
  border-radius: 10px;
  padding: 10px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);

  img {
    display: block;
    width: 180px;
    height: 180px;
    border-radius: 4px;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 8px 0 14px;
  color: #94a3b8;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: #e2e8f0;
  }
`;

const CodeBox = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px 14px;
  font-family: "SF Mono", Monaco, Menlo, Consolas, monospace;
  font-size: 12.5px;
  color: #334155;
  line-height: 1.5;
  word-break: break-all;
  margin-bottom: 14px;
  user-select: all;
`;

const PrimaryButton = styled.button`
  width: 100%;
  padding: 12px 14px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: ${(p) => p.$color || "#0f172a"};
  color: #fff;
  transition: background 0.15s, transform 0.1s;

  &:hover:not(:disabled) {
    background: ${(p) =>
      p.$color ? darkenColor(p.$color, 12) : "#1e293b"};
  }
  &:active:not(:disabled) {
    transform: translateY(1px);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  width: 100%;
  padding: 11px 14px;
  margin-top: 8px;
  background: transparent;
  color: #475569;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.15s, border-color 0.15s;

  &:hover:not(:disabled) {
    background: #f8fafc;
    border-color: #cbd5e1;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Spinner = styled(Loader2)`
  animation: ${spin} 0.9s linear infinite;
`;

const Footer = styled.div`
  text-align: center;
  padding: 12px 20px 18px;
  font-size: 11px;
  color: #94a3b8;
  border-top: 1px solid #f1f5f9;
`;

// ─── Component ───────────────────────────────────────────────────────────────

const PixPaymentModal = ({
  itemDetails,
  response,
  checkoutPageConfDto,
  latestSession,
  // "pagbrasil" returns amount in major units already; all other gateways
  // (e.g. dlocal) return minor units and require conversion.
  gateway = "dlocal",
}) => {
  const { t, language } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [showCheckStatus, setShowCheckStatus] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [isProcessingVisible, setIsProcessingVisible] = useState(false);

  const transactionId =
    response?.newTransactionId || response?.transactionId || "";
  const hostedPageRedirect =
    response?.hosted_redirect_page ||
    response?.hosted_page_redirect ||
    null;
  const fallbackRedirectUrl =
    latestSession?.redirectUrl ||
    response?.front_end_redirect_url ||
    checkoutPageConfDto?.checkoutRedirectUrl ||
    null;

  const merchantName =
    checkoutPageConfDto?.companyName ||
    checkoutPageConfDto?.brand ||
    window.location.hostname.split(".")[0];
  const merchantLogo = checkoutPageConfDto?.logoUrl;
  const brandColor = checkoutPageConfDto?.colour;
  const merchantInitial = (merchantName || "M").substring(0, 1).toUpperCase();

  const currency = itemDetails?.currencyCode || "BRL";
  // dLocal (and most gateways) return `amount` in minor units (e.g. 49900 for
  // BRL 499) so we convert. PagBrasil is the exception: it returns major units
  // (e.g. 499.0) so we parse directly without dividing by 100.
  const amountMajor =
    gateway === "pagbrasil"
      ? parseFloat(response?.amount || 0)
      : minorToMajor(response?.amount, currency);
  const formattedAmount = formatCurrency(amountMajor, currency, language);
  const isConsentOnly =
    response?.pix_consent_only === true ||
    response?.pix_consent_only === "true";
  const hasQrCode = !!response?.qrCodeUrl;

  useWebSocket(
    isOpen && transactionId ? transactionId : null,
    (event) => {
      try {
        const data = JSON.parse(event.data);
        if (!data?.status || data.status === "pending") return;

        const normalizedStatus = String(data.status).startsWith("failed")
          ? "failed"
          : data.status;

        if (normalizedStatus !== "success" && normalizedStatus !== "failed") {
          return;
        }

        setPaymentStatus(normalizedStatus);
        setShowTransactionModal(true);

        if (normalizedStatus === "success") {
          toast.success(t("messages.success.transactionSuccess"));
        } else {
          toast.error(t("messages.error.paymentFailed"));
        }
      } catch (error) {
        console.error("Pix WebSocket message parsing failed:", error);
      }
    }
  );

  useEffect(() => {
    setIsOpen(true);
  }, []);

  const handleCopySecret = async () => {
    const text = response?.qrSecret || "";
    if (!text) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 1500);
    } catch (err) {
      console.error("Clipboard error:", err);
      toast.error(t("messages.error.somethingWrong"));
    }
  };

  const handleSimulateSandbox = () => {
    safeRedirect(response?.redirect_url, { target: "blank" });
    setShowCheckStatus(true);
  };

  const checkPaymentStatus = async () => {
    const trxId = response?.newTransactionId;
    if (!trxId) return;

    setIsProcessingVisible(true);
    setPaymentStatus(null);
    setStatusLoading(true);

    try {
      const apiResponse = await api.get(
        `/api/checkout/v1/transaction/status/${trxId}`
      );
      const status = apiResponse?.data?.status || apiResponse?.data;
      setPaymentStatus(status);
      setIsProcessingVisible(false);

      if (status === "success") {
        toast.success(t("messages.success.transactionSuccess"));
      } else if (status === "failed") {
        toast.error(t("messages.error.paymentFailed"));
      } else {
        toast.info(t("messages.info.transactionPending"));
      }
      setTimeout(() => setShowTransactionModal(true), 200);
    } catch (error) {
      console.error("Pix status check failed:", error);
      toast.error(t("messages.error.somethingWrong"));
      setIsProcessingVisible(false);
    } finally {
      setStatusLoading(false);
    }
  };

  // Poll transaction status while the PIX modal is open. The WebSocket push is the
  // primary channel, but it can drop while the user pays in their banking app; this
  // silent poll guarantees the page eventually reflects the real status.
  useEffect(() => {
    if (!isOpen || !transactionId) return undefined;
    if (paymentStatus === "success" || paymentStatus === "failed") return undefined;

    let stopped = false;

    const poll = async () => {
      try {
        const apiResponse = await api.get(
          `/api/checkout/v1/transaction/status/${transactionId}`
        );
        if (stopped) return;

        const status = apiResponse?.data?.status || apiResponse?.data;
        if (status === "success" || status === "failed") {
          setPaymentStatus(status);
          setShowTransactionModal(true);
          if (status === "success") {
            toast.success(t("messages.success.transactionSuccess"));
          } else {
            toast.error(t("messages.error.paymentFailed"));
          }
        }
      } catch (error) {
        // Silent — polling is a best-effort fallback, next tick will retry.
        console.error("Pix status poll failed:", error);
      }
    };

    const interval = setInterval(poll, 5000);
    return () => {
      stopped = true;
      clearInterval(interval);
    };
  }, [isOpen, transactionId, paymentStatus, t]);

  // When the user returns to the tab after sandbox redirect, ensure check
  // status button is shown.
  useEffect(() => {
    const handleVisibility = () => {
      if (
        document.visibilityState === "visible" &&
        isOpen &&
        showCheckStatus &&
        !statusLoading &&
        !paymentStatus
      ) {
        setShowCheckStatus(true);
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, [isOpen, showCheckStatus, statusLoading, paymentStatus]);

  return (
    <>
      {/* Status pipeline — these will route through SkinModalContext when
          a skin (e.g. Neo) is active, so the user sees the skin's overlays. */}
      <PaymentProcessingModal
        show={isProcessingVisible}
        setShow={setIsProcessingVisible}
        transactionId={transactionId}
        setTransactionStatus={setPaymentStatus}
        setTransactionStatusModal={setShowTransactionModal}
      />
      <TransactionStatusModal
        show={showTransactionModal}
        onClose={() => setShowTransactionModal(false)}
        status={paymentStatus}
        transactionId={transactionId}
        hostedPageRedirect={hostedPageRedirect}
        fallbackRedirectUrl={fallbackRedirectUrl}
      />

      {isOpen && (
        <Overlay role="dialog" aria-modal="true">
          <Card onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <Header>
              <MerchantBlock>
                <LogoCircle $color={brandColor}>
                  {merchantLogo ? (
                    <img src={merchantLogo} alt={merchantName} />
                  ) : (
                    <LogoInitial>{merchantInitial}</LogoInitial>
                  )}
                </LogoCircle>
                <MerchantName>{merchantName}</MerchantName>
              </MerchantBlock>
              <CloseButton
                type="button"
                onClick={() => setShowWarning(true)}
                aria-label="Close"
              >
                <X size={18} />
              </CloseButton>
            </Header>

            <Body>
              <PixBadge>
                <img src={pixLogo} alt="Pix" />
              </PixBadge>

              {isConsentOnly ? (
                <>
                  <QrInstruction>{t("pix.consentTitle")}</QrInstruction>
                  <ExpiryLine>{t("pix.consentInstruction")}</ExpiryLine>
                  <Amount>{t("pix.noPaymentDue")}</Amount>
                </>
              ) : (
                <>
                  <Amount>{formattedAmount}</Amount>
                  {response?.expiryDate && (
                    <ExpiryLine>
                      {t("pix.expirationDate")} ·{" "}
                      {formatDateOnly(response.expiryDate)}
                    </ExpiryLine>
                  )}
                </>
              )}

              {hasQrCode && (
                <QrCard>
                  <QrInstruction>
                    {isConsentOnly ? t("pix.consentScanQrCode") : t("pix.scanQrCode")}
                  </QrInstruction>
                  <QrFrame>
                    <img
                      src={response?.qrCodeUrl}
                      alt={t("pix.qrCodeAlt")}
                    />
                  </QrFrame>
                </QrCard>
              )}

              {response?.qrSecret && (
                <>
                  <Divider>{t("pix.copyInstruction")}</Divider>
                  <CodeBox>{response.qrSecret}</CodeBox>
                  <PrimaryButton
                    type="button"
                    onClick={handleCopySecret}
                    $color={brandColor}
                  >
                    {isCopied ? (
                      <>
                        <Check size={16} />
                        {t("pix.copying")}
                      </>
                    ) : (
                      <>
                        <Copy size={16} />
                        {t("pix.copyPaste")}
                      </>
                    )}
                  </PrimaryButton>
                </>
              )}

              {response?.redirect_url &&
                (!showCheckStatus ? (
                  <SecondaryButton
                    type="button"
                    onClick={handleSimulateSandbox}
                  >
                    {isConsentOnly ? t("pix.consentSandbox") : t("pix.sendToSandbox")}
                  </SecondaryButton>
                ) : (
                  <SecondaryButton
                    type="button"
                    onClick={checkPaymentStatus}
                    disabled={statusLoading}
                  >
                    {statusLoading ? (
                      <>
                        <Spinner size={14} />
                        {t("pix.checking")}
                      </>
                    ) : (
                      t("pix.checkStatus")
                    )}
                  </SecondaryButton>
                ))}

              {isConsentOnly && !response?.redirect_url && (
                <PrimaryButton
                  type="button"
                  onClick={checkPaymentStatus}
                  disabled={statusLoading}
                  $color={brandColor}
                >
                  {statusLoading ? (
                    <>
                      <Spinner size={16} />
                      {t("pix.checking")}
                    </>
                  ) : (
                    t("pix.checkStatus")
                  )}
                </PrimaryButton>
              )}
            </Body>

            <Footer>{t("checkout.neo.poweredBy") || "Powered by Altruon"}</Footer>
          </Card>
        </Overlay>
      )}

      <WarningModal
        isOpen={showWarning}
        onClose={() => setShowWarning(false)}
        onConfirm={() => {
          setShowWarning(false);
          setIsOpen(false);
          window.location.reload();
        }}
        title={t("modals.warning.closePaymentTitle")}
        message={t("modals.warning.closePaymentMessage")}
        confirmText={t("modals.warning.yesClose")}
        cancelText={t("modals.warning.stayHere")}
        confirmButtonColor="#dc2626"
      />
    </>
  );
};

export default PixPaymentModal;
